import { EnvironmentConfiguration, InfraMap, InfraSettings } from "./env-infra";

// in environment vars, urls are be separated by commas
const URL_SPLITTER = ",";

/*
 * The goal of this method is to provide the same "map" of backend hosts per frontend hosts as was initially setup.
 * But by extracting data from environment variables instead of
 */
export const createInfraMapFromEnv = (env: NodeJS.ProcessEnv): InfraMap => {
    const frontendHosts = env["RDR_FRONTEND_HOSTS"] || globalThis.location.host;
    const backendHosts =
        env["RDR_BACKEND_HOSTS"] ||
        env["RDR_BACKEND_HOSTS_FROM_BUILD_ENV"] ||
        "";
    const backendSSRHosts = env["RDR_SSR_BACKEND_HOSTS"] || backendHosts;
    const gatewaySSRHosts = env["RDR_SSR_HCOMGATEWAY_HOSTS"] || backendSSRHosts;
    const assetsHosts = env["RDR_ASSETS_HOSTS"] || "//assets.hermes.com";

    const personalizationPlatformeUrl =
        env["RDR_PERSO_PLATEFORME_URL"] ||
        "https://ripe-core-stage.platforme.com/api";

    const environment = env["APP_ENV"] || "localhost";

    const datadomeKey =
        env["RDR_DATADOME_KEY"] || "028ED961A95E7551C859E3B2E0A5EF";

    const faroConfiguration =
        env["RDR_FARO_URL"] && env["RDR_FARO_API_KEY"]
            ? {
                  url: env["RDR_FARO_URL"],
                  apiKey: env["RDR_FARO_API_KEY"],
              }
            : undefined;

    const sensorsdata = env["RDR_SENSORS_DATA_URL"] || undefined;
    const externalTrustedHosts = env["RDR_EXTERNAL_TRUSTED_HOSTS"]?.split(
        URL_SPLITTER,
    ) || ["bookings-demo.qudini.com"];

    const frontendArray = frontendHosts.split(URL_SPLITTER);
    const backendCSRArray = backendHosts.split(URL_SPLITTER);
    const backendSSRHostsArray = backendSSRHosts.split(URL_SPLITTER);
    const gatewaySSRHostsArray = gatewaySSRHosts.split(URL_SPLITTER);
    const assetsArray = assetsHosts.split(URL_SPLITTER);

    // oxlint-disable-next-line unicorn/no-array-reduce
    return frontendArray.reduce(
        (
            accumulator: Record<string, InfraSettings>,
            currentValue: string,
            currentIndex: number,
        ) => {
            const hostKey = currentValue.replace(/https?:\/\//, "");
            return {
                ...accumulator,
                [hostKey]: {
                    frontend: frontendArray[currentIndex],
                    backendCSR: backendCSRArray[currentIndex],
                    backendSSR: normalizeUrl(
                        backendSSRHostsArray[currentIndex],
                    ),
                    hcomGatewaySSR: normalizeUrl(
                        gatewaySSRHostsArray[currentIndex],
                    ) as string,
                    assets: assetsArray[currentIndex],
                    personalizationPlatformeUrl,
                    environment,
                    datadomeKey,
                    externalTrustedHosts,
                    faroConfiguration,
                    sensorsdata,
                },
            };
        },
        {},
    );
};

/*
 * URL should be normalize to remove default ports such as ":80" or ":443".
 * This keeps the URL consistent with Angular's internal normalization used by HttpTransferCache.
 */
const normalizeUrl = (url: string | undefined): string | undefined => {
    if (!url) {
        return undefined;
    }

    // On local CSR, url is relative ("/bck")
    return URL.canParse(url) ? new URL(url).origin : url;
};

export const envConfigFactory = (
    env: NodeJS.ProcessEnv,
): EnvironmentConfiguration => ({
    appName: "RDR",
    appVersion: env["APP_VERSION"],
    environmentKey: env["APP_ENV"],
    gitBranch: env["BRANCH_NAME"] || "develop",
    gitCommit: env["GIT_COMMIT"] || "xxxxxxxx",
    hosts: createInfraMapFromEnv(env),
});
